import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Apple, Play, ArrowDown } from "lucide-react";
import { MaskLines } from "./Reveal";

const HERO_IMG =
  "https://images.pexels.com/photos/13311393/pexels-photo-13311393.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden px-6 pt-28 pb-20 md:px-12"
    >
      {/* ambient volt glow */}
      <div className="pointer-events-none absolute -top-40 right-[-10%] h-[520px] w-[520px] rounded-full bg-brand-volt/10 blur-[120px]" />

      <motion.div style={{ y: textY, opacity: fade }} className="mx-auto w-full max-w-[1400px]">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-brand-muted"
        >
          <span className="h-px w-10 bg-brand-volt" />
          Hyper-local delivery · from your neighborhood seller
        </motion.p>

        <div className="font-display uppercase leading-[0.82] tracking-tight text-brand-bone">
          <MaskLines
            lines={["Leading"]}
            lineClassName="text-[16vw] md:text-[12vw]"
            start={0.25}
          />

          {/* line with inline pill image */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <div className="overflow-hidden">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="block text-[16vw] md:text-[12vw]"
              >
                Doorstep
              </motion.span>
            </div>
            <motion.div
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              transition={{ duration: 1.1, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="relative h-[9vw] min-h-[54px] w-[26vw] min-w-[150px] origin-left overflow-hidden rounded-full border border-white/10"
            >
              <motion.img
                style={{ y: imgY }}
                src={HERO_IMG}
                alt="Zeroomile rider delivering across the neighborhood"
                className="h-[140%] w-full object-cover"
              />
              <div className="absolute inset-0 bg-black/10" />
            </motion.div>
          </div>

          <MaskLines
            lines={[
              <span key="d">
                Delivery<span className="text-brand-volt">.</span>
              </span>,
            ]}
            lineClassName="text-[16vw] md:text-[12vw]"
            start={0.55}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-10 flex flex-col items-start gap-8 md:flex-row md:items-center md:justify-between"
        >
          <p className="max-w-md text-base leading-relaxed text-brand-muted md:text-lg">
            Fast, convenient ordering powered by the{" "}
            <span className="font-serif italic text-brand-bone">BRIGHTSPOT</span> network —
            turning every local store into a beacon of instant service.
          </p>

          <div className="flex flex-wrap gap-3">
            <a
              href="#download"
              data-testid="hero-google-play"
              className="group flex items-center gap-3 rounded-2xl border border-brand-line bg-brand-surface px-5 py-3 transition-colors duration-300 hover:border-brand-volt"
            >
              <Play size={22} className="text-brand-volt" fill="currentColor" />
              <span className="text-left">
                <span className="block text-[10px] uppercase tracking-widest text-brand-muted">Get it on</span>
                <span className="block font-medium leading-tight">Google Play</span>
              </span>
            </a>
            <a
              href="#download"
              data-testid="hero-app-store"
              className="group flex items-center gap-3 rounded-2xl border border-brand-line bg-brand-surface px-5 py-3 transition-colors duration-300 hover:border-brand-volt"
            >
              <Apple size={24} className="text-brand-bone" />
              <span className="text-left">
                <span className="block text-[10px] uppercase tracking-widest text-brand-muted">Download on the</span>
                <span className="block font-medium leading-tight">App Store</span>
              </span>
            </a>
          </div>
        </motion.div>
      </motion.div>

      {/* scroll indicator */}
      <motion.div
        style={{ opacity: fade }}
        className="pointer-events-none absolute bottom-8 right-6 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-brand-muted md:right-12"
      >
        Scroll to discover
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
        >
          <ArrowDown size={16} className="text-brand-volt" />
        </motion.span>
      </motion.div>
    </section>
  );
};
